"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { supabase } from "@/integrations/supabase/client";
import { parseRecipeUrl, generateRecipeUrl } from "@/lib/urlUtils";
import { Navigation } from "@/components/Navigation";
import { Recipe, Ingredient } from "@/components/RecipeCard";
import { RecipeContent } from "@/components/RecipeContent";
import { ShareButton } from "@/components/ShareButton";
import { StructuredData } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
// Tree-shaken icon imports for optimal bundle size
import Bookmark from "lucide-react/dist/esm/icons/bookmark";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { RecipeStructuredData } from "@/lib/structuredData";
import { getImageUrl } from '@/lib/imageUtils';
import { checkDuplicateRecipe } from "@/lib/utils";

interface RecipeViewClientProps {
    recipeId: string;
}

export default function RecipeViewClient({ recipeId }: RecipeViewClientProps) {
    const router = useRouter();
    const { user } = useAuth();
    const { toast } = useToast();
    const [recipe, setRecipe] = useState<Recipe | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [shareUrl, setShareUrl] = useState('');

    useEffect(() => {

        const loadRecipe = async () => {
            try {
                const parsedResult = parseRecipeUrl(recipeId);
                let data = null;
                let error = null;

                // Try slug-based query first (new SEO-friendly system)
                if (parsedResult?.slug && parsedResult.sequenceNumber !== undefined) {
                    const result = await (supabase as any)
                        .from('saved_recipes')
                        .select('*')
                        .eq('recipe_slug', parsedResult.slug)
                        .eq('sequence_number', parsedResult.sequenceNumber)
                        .eq('is_public', true)
                        .eq('is_deleted', false)
                        .maybeSingle();

                    data = result.data;
                    error = result.error;
                }

                // Fallback: Try direct UUID lookup for legacy URLs
                if (!data && parsedResult?.slug && parsedResult.sequenceNumber === undefined) {
                    const result = await (supabase as any)
                        .from('saved_recipes')
                        .select('*')
                        .eq('id', parsedResult.slug)
                        .eq('is_public', true)
                        .eq('is_deleted', false)
                        .maybeSingle();

                    data = result.data;
                    error = result.error;
                }

                // Fallback: Try to load by UUID for legacy recipes
                if (!data && recipeId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    const result = await (supabase as any)
                        .from('saved_recipes')
                        .select('*')
                        .eq('id', recipeId)
                        .eq('is_public', true)
                        .eq('is_deleted', false)
                        .maybeSingle();

                    data = result.data;
                    error = result.error;
                }

                if (error || !data) {
                    setIsLoading(false);
                    return;
                }

                // Transform database data to Recipe type
                const recipeData: Recipe = {
                    id: data.id,
                    title: data.title,
                    ingredients: (data.ingredients as unknown) as Ingredient[],
                    calories_per_100g: data.calories_per_100g,
                    carbs_per_100g: data.carbs_per_100g,
                    protein_per_100g: data.protein_per_100g,
                    fat_per_100g: data.fat_per_100g,
                    estimated_prep_time: data.estimated_prep_time,
                    estimated_total_time: data.estimated_total_time,
                    preparation: data.preparation,
                    visual_dish_description_en: data.visual_dish_description_en,
                    dish_origin_context_he: data.dish_origin_context_he,
                    health_summary_he: data.health_summary_he,
                    image: data.image || undefined,
                    user_id: data.user_id,
                    is_public: data.is_public,
                    share_token: data.share_token,
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                    is_temporary: false,
                    status: 'complete',
                    recipe_slug: (data as any).recipe_slug || undefined,
                    sequence_number: (data as any).sequence_number || undefined,
                    version: (data as any).version || undefined,
                    recipe_quality_score: data.recipe_quality_score || undefined,
                    quality_explanation_he: data.quality_explanation_he || undefined
                };

                setRecipe(recipeData);

                // Generate share URL
                const origin = typeof window !== 'undefined' ? window.location.origin : '';

                // Try to generate SEO-friendly URL if recipe has slug and sequence
                if (recipeData.recipe_slug && recipeData.sequence_number) {
                    try {
                        const recipeUrl = generateRecipeUrl(
                            recipeData.id!,
                            recipeData.title,
                            recipeData.sequence_number,
                            recipeData.version
                        );
                        setShareUrl(`${origin}${recipeUrl}`);
                    } catch (err) {
                        // Fallback to simple ID-based URL
                        setShareUrl(`${origin}/recipe/${recipeData.id}`);
                    }
                } else {
                    // Fallback to simple ID-based URL for recipes without slug/sequence
                    setShareUrl(`${origin}/recipe/${recipeData.id}`);
                }
            } catch (error) {
                console.error('Error loading recipe:', error);
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadRecipe();
    }, [recipeId, router]);

    // Check for duplicates when recipe changes
    useEffect(() => {
        if (recipe && user) {
            const checkDuplicate = async () => {
                try {
                    console.log('🔍 Checking for duplicate recipe:', {
                        title: recipe.title,
                        userId: user.id,
                        ingredientsCount: recipe.ingredients.length
                    });
                    const isDup = await checkDuplicateRecipe(recipe.title, recipe.ingredients, user.id);
                    console.log('📊 Duplicate check result:', isDup);
                    setIsDuplicate(isDup);
                } catch (error) {
                    console.error("Error checking for duplicates:", error);
                    setIsDuplicate(false); // Allow saving if check fails
                }
            };
            checkDuplicate();
        } else {
            // Reset duplicate state if no recipe or user
            setIsDuplicate(false);
        }
    }, [recipe, user]);

    const handleSave = async () => {
        if (!recipe || !user) {
            toast({
                title: "נדרשת התחברות",
                description: "התחברו כדי לשמור מתכונים",
                variant: "destructive",
            });
            return;
        }

        // Check if duplicate (should be caught by disabled button, but double-check)
        if (isDuplicate) {
            toast({
                title: "המתכון כבר קיים",
                description: "המתכון הזה כבר נשמר בספר המתכונים שלך",
                variant: "destructive",
            });
            return;
        }

        setIsSaving(true);
        try {
            // INSERT new recipe copy (from public recipe)
            const { data: savedRecipe, error } = await (supabase as any)
                .from('saved_recipes')
                .insert({
                    user_id: user.id,
                    title: recipe.title,
                    ingredients: recipe.ingredients,
                    preparation: recipe.preparation,
                    estimated_prep_time: recipe.estimated_prep_time,
                    estimated_total_time: recipe.estimated_total_time,
                    calories_per_100g: recipe.calories_per_100g,
                    carbs_per_100g: recipe.carbs_per_100g,
                    protein_per_100g: recipe.protein_per_100g,
                    fat_per_100g: recipe.fat_per_100g,
                    visual_dish_description_en: recipe.visual_dish_description_en || null,
                    health_summary_he: recipe.health_summary_he || null,
                    dish_origin_context_he: recipe.dish_origin_context_he || null,
                    image: getImageUrl(recipe.image),
                    is_temporary: false,
                    is_public: false
                })
                .select()
                .single();

            if (error) throw error;

            console.log(`✅ Created new recipe copy:`, savedRecipe);

            // Trigger image generation if recipe doesn't have an image yet and has visual description
            if (savedRecipe && !savedRecipe.image && savedRecipe.visual_dish_description_en) {
                console.log(`🎨 Generating image for recipe ${savedRecipe.id}`);

                supabase.functions.invoke('generate-optimized-image', {
                    body: {
                        recipe_id: savedRecipe.id,
                        title: savedRecipe.title,
                        user_id: user.id,
                        visual_dish_description_en: savedRecipe.visual_dish_description_en
                    }
                }).catch(err => console.error('Image generation error:', err));
            }

            toast({
                title: "המתכון נשמר!",
                description: "המתכון נוסף לספר המתכונים שלך",
            });

            // Update duplicate state
            setIsDuplicate(true);

            // Navigate to cookbook
            router.push('/cookbook');
        } catch (error) {
            console.error("Error saving recipe:", error);
            toast({
                title: "שגיאה בשמירת המתכון",
                description: "לא הצלחנו לשמור את המתכון. נסה שוב מאוחר יותר.",
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
                <Navigation />
                <div className="flex items-center justify-center h-[calc(100vh-64px)]">
                    <div className="text-center" dir="rtl">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">טוען מתכון...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
                <Navigation />
                <div className="container mx-auto px-4 py-8 max-w-4xl">
                    <div className="text-center" dir="rtl">
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">המתכון לא נמצא</h1>
                        <p className="text-gray-600 mb-6">המתכון שחיפשת לא נמצא או שהוסר</p>
                        <Button onClick={() => router.push('/')}>חזרה לדף הבית</Button>
                    </div>
                </div>
            </div>
        );
    }

    // Generate structured data for SEO
    const structuredData: RecipeStructuredData | null = recipe ? {
        title: recipe.title,
        description: recipe.health_summary_he || recipe.dish_origin_context_he || `מתכון טעים ל${recipe.title} עם הוראות מפורטות ומידע תזונתי`,
        image: getImageUrl(recipe.image),
        ingredients: recipe.ingredients.map(ing => ({
            name: ing.name,
            amount: ing.amount || 'לפי הטעם'
        })),
        instructions: recipe.preparation,
        prepTime: recipe.estimated_prep_time,
        totalTime: recipe.estimated_total_time,
        calories: recipe.calories_per_100g,
        protein: recipe.protein_per_100g,
        carbs: recipe.carbs_per_100g,
        fat: recipe.fat_per_100g,
        author: "Feedly",
        datePublished: recipe.created_at,
        dateModified: recipe.updated_at,
        url: shareUrl,
        keywords: `${recipe.title}, מתכון, בישול, עברית, ${recipe.ingredients.slice(0, 3).map(ing => ing.name).join(', ')}`
    } : null;

    // Generate breadcrumb data for SEO
    const breadcrumbData = [
        { name: 'בית', url: 'https://feedly.co.il/' },
        { name: 'מתכונים', url: 'https://feedly.co.il/cookbook' },
        { name: recipe.title, url: shareUrl }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
            {/* SEO Structured Data */}
            {structuredData && (
                <StructuredData type="recipe" data={structuredData} />
            )}

            {/* Breadcrumb Structured Data */}
            <StructuredData type="breadcrumb" data={breadcrumbData} />

            <Navigation />

            <div className="container mx-auto px-4 py-8 max-w-4xl">
                {/* Recipe Card - Wrapped in styled container */}
                <div className="bg-white rounded-2xl shadow-lg border border-orange-200/40 overflow-hidden">
                    {/* Recipe Header */}
                    <div className="px-6 pt-6 pb-0" dir="rtl">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4 text-right">{recipe.title}</h1>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3 py-3 border-b border-orange-200/30" dir="rtl">
                            {/* Save Button - Always rightmost (left in RTL) */}
                            <Button
                                onClick={handleSave}
                                disabled={isSaving || isDuplicate}
                                size="sm"
                                variant="default"
                                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white"
                            >
                                {isSaving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Bookmark className="h-4 w-4" />
                                )}
                                שמור
                            </Button>

                            {/* Share Button - Left of save (right in RTL) */}
                            <ShareButton
                                url={shareUrl}
                                title={recipe.title}
                                description={`בוא נבשל ביחד! ${recipe.title}`}
                                variant="outline"
                                size="sm"
                                showDropdown={true}
                            />
                        </div>
                    </div>

                    {/* Recipe Content */}
                    <div className="px-6 pb-6">
                        <RecipeContent
                            recipe={recipe}
                            isLoading={false}
                            showQualitySection={false}
                            showActionButtons={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
